import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AuthShell } from '../components/auth/AuthShell.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { PasswordInput } from '../components/ui/password-input.js';
import { useAuthStore } from '../stores/auth.js';
import { isSafeRedirect } from '../features/auth/guards.js';
import { loginMutation, fetchMeMutation } from '../features/auth/api.js';
import { ApiError, mapApiError, mapKnownApiError } from '../lib/api/error.js';
import { consumeForcedLogout } from '../lib/api/forced-logout.js';
import { useBrandingStore } from '../stores/branding.js';
import { TurnstileWidget } from '../components/captcha/TurnstileWidget.js';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_auth/login')({
  component: LoginPage,
});

// Dev convenience: seed creds match scripts/seed-e2e-data.ts. Runtime
// hostname check (not `import.meta.env.DEV`) so the banner survives
// preview/HMR-cached chunks where the build-time flag can flip to false.
function getDevAutofill(): { identifier: string; password: string } | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
    return { identifier: 'thab00010', password: 'password123' };
  }
  return null;
}
const DEV_AUTOFILL = getDevAutofill();

function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setSubject = useAuthStore((s) => s.setSubject);
  const { t } = useTranslation('auth');
  const sitename = useBrandingStore((s) => s.branding?.sitename) ?? '';
  const captcha = useBrandingStore((s) => s.branding?.captcha);
  const captchaOn = Boolean(captcha?.enabled && captcha?.siteKey);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);
  const [captchaEpoch, setCaptchaEpoch] = useState(0);
  const [username, setUsername] = useState(DEV_AUTOFILL?.identifier ?? '');
  const [password, setPassword] = useState(DEV_AUTOFILL?.password ?? '');
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);

  // Turnstile tokens are single-use — after every submit (success handled by
  // navigation, failure by retry) the widget must remount to issue a fresh one.
  function resetCaptcha() {
    setCaptchaToken(undefined);
    setCaptchaEpoch((n) => n + 1);
  }

  const redirect =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('redirect') ?? undefined
      : undefined;

  // Force-logout reason stashed by apiFetch's refresh-failure path (e.g. a
  // revoked session because this account signed in elsewhere) — surfaced once
  // here since the redirect that follows a clear() leaves no live component
  // to toast from at the moment it actually happens. The code goes through the
  // shared catalog rather than a hardcoded match, so a backend reason added
  // tomorrow reaches the player as soon as it has a catalog entry; one the
  // catalog cannot name stays silent, since the generic "something went wrong"
  // would be untrue on a page the player has only just landed on.
  useEffect(() => {
    const message = mapKnownApiError(consumeForcedLogout(), t);
    if (message) toast.error(message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      loginMutation({
        identifier: username,
        password,
        captchaToken,
        ...(needsMfa && /^\d{6}$/.test(mfaCode) ? { mfaCode } : {}),
      }),
    onSuccess: async (result) => {
      setAuth({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken ?? null,
      });

      const user = result.user;
      if (user?.id) {
        setSubject({
          id: user.id,
          kind: 'player',
          agentId: user.agentId ?? '',
          currency: 'THB',
          displayName: user.username,
        });
      } else {
        try {
          const subject = await fetchMeMutation();
          setSubject(subject);
        } catch {
          // non-fatal — guards rely on accessToken, not subject
        }
      }

      toast.success(t('login.welcomeBack'));
      const dest = isSafeRedirect(redirect) ? redirect : '/';
      void navigate({ to: dest as '/' });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'AUTH-MFA-REQUIRED') {
        setNeedsMfa(true);
        setMfaCode('');
        resetCaptcha();
        return;
      }
      toast.error(mapApiError(err, t));
      resetCaptcha();
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <AuthShell>
          {DEV_AUTOFILL && (
            <div className="text-xs px-3 py-2 rounded-lg bg-[var(--warning-bg)] text-[var(--warning)] font-mono leading-relaxed">
              dev auto-fill: {DEV_AUTOFILL.identifier} / {DEV_AUTOFILL.password}
            </div>
          )}
          <div>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight">
              {needsMfa ? t('login.mfaTitle') : t('login.welcomeBack')}
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-2">
              {needsMfa ? t('login.mfaSubtitle') : t('login.subtitle', { sitename })}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {!needsMfa && (
              <>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                    {t('login.username')}
                  </label>
                  <Input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    placeholder={t('login.usernamePlaceholder')}
                    required
                    minLength={1}
                    containerClassName="h-12"
                    className="text-base"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                    {t('login.password')}
                  </label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    // Presence, not policy — this password is verified, never
                    // stored. A floor here blocks submit in the browser before
                    // the request is even sent.
                    minLength={1}
                    containerClassName="h-12"
                    className="text-base"
                  />
                  <Link
                    to="/forgot"
                    className="text-[13px] text-[var(--text-secondary)] hover:text-[var(--primary)] self-end mt-1"
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              </>
            )}

            {needsMfa && (
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">
                  {t('login.mfaCodeLabel')}
                </label>
                <Input
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  placeholder={t('login.mfaCodePlaceholder')}
                  required
                  containerClassName="h-12"
                  className="text-base tracking-[0.3em] font-mono"
                />
                <button
                  type="button"
                  onClick={() => {
                    setNeedsMfa(false);
                    setMfaCode('');
                  }}
                  className="text-[13px] text-[var(--text-secondary)] hover:text-[var(--primary)] self-end mt-1"
                >
                  {t('login.mfaBack')}
                </button>
              </div>
            )}

            {captchaOn && captcha?.siteKey && (
              <TurnstileWidget
                key={captchaEpoch}
                siteKey={captcha.siteKey}
                onSolve={setCaptchaToken}
              />
            )}

            <Button
              type="submit"
              size="lg"
              glow
              fullWidth
              disabled={
                mutation.isPending ||
                (captchaOn && !captchaToken) ||
                (needsMfa && !/^\d{6}$/.test(mfaCode))
              }
              className="h-14 text-base"
            >
              {mutation.isPending
                ? t('login.submitting')
                : needsMfa
                  ? t('login.mfaSubmit')
                  : t('login.submit')}
            </Button>

            <Button asChild variant="ghost" fullWidth>
              <Link to="/">{t('backToLobby')}</Link>
            </Button>
          </form>

          <div className="text-center text-sm text-[var(--text-secondary)] pt-1">
            {t('login.noAccount', { sitename })}{' '}
            <Link to="/register" className="text-[var(--primary)] font-semibold hover:underline">
              {t('login.createAccount')}
            </Link>
          </div>
    </AuthShell>
  );
}
